import { Button, Input } from '@popsure/dirty-swan';
import { ChangeEvent, FormEvent } from 'react';
import { useSafeTranslation } from 'shared/i18n';

import { getErrorMessageTranslation } from '../../utils/getApiErrors';
import styles from './Email.module.scss';
import { EmailSentConfirmationView } from './EmailSentConfirmation.view';

type EmailViewProps = {
  email: string;
  wasEmailSent: boolean;
  onSubmit: (event: FormEvent) => void;
  handleOnEmailChange: (event: ChangeEvent<HTMLInputElement>) => void;
  isLoading: boolean;
  error: unknown | null;
  onResendVerification: () => void;
};

export const EmailView = ({
  wasEmailSent,
  email,
  onSubmit,
  handleOnEmailChange,
  isLoading,
  error,
  onResendVerification,
}: EmailViewProps) => {
  const { t } = useSafeTranslation();

  if (wasEmailSent) {
    return (
      <EmailSentConfirmationView
        email={email}
        onResendVerification={onResendVerification}
      />
    );
  }

  return (
    <div className={styles.container}>
      <div className="p-body">
        <form onSubmit={onSubmit}>
          <h1 className="p-h1">
            {t('updateEmail.enterEmail.title', 'Enter your new email address')}
          </h1>
          <div className="p-p mt8 wmx8">
            {t(
              'updateEmail.enterEmail.subtitle',
              'A verification link will be sent to this email address in order to verify it. Once verified you will be logged out of all your devices.'
            )}{' '}
          </div>
          <Input
            className={`wmn3 mt16 ${styles.input}`}
            value={email}
            type="email"
            data-testid="update-email-login-code-input"
            placeholder={t(
              'updateEmail.enterEmail.input.placeholder',
              'New email address'
            )}
            onChange={handleOnEmailChange}
            required
            data-cy="update-email-login-code-input"
          />
          {error && (
            <div className="p-notice p-notice--danger mt16 wmx5">
              {getErrorMessageTranslation(t, error)}
            </div>
          )}
          <Button
            className="wmn3 mt16"
            type="submit"
            disabled={email.length === 0}
            loading={isLoading}
          >
            {t('updateEmail.enterEmail.button.text', 'Send verification email')}
          </Button>
        </form>
      </div>
    </div>
  );
};
